import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { useLocationStore } from '../stores/locationStore'
import { getCurrentLocation } from '../lib/utils'

export function useLocation() {
  const {
    latitude,
    longitude,
    error,
    isLoading,
    permissionState,
    setLocation,
    setError,
    setIsLoading,
    setPermissionState,
  } = useLocationStore()

  useEffect(() => {
    if (permissionState === 'granted') {
      // Already granted from persisted state — fetch fresh coords silently
      fetchLocation()
    } else {
      // Unknown or denied — check via Permissions API
      checkPermissionState()
    }
  }, [])

  async function checkPermissionState() {
    // Check current permission state without triggering a prompt.
    // Only auto-fetch if already granted — everything else requires a user gesture.
    try {
      if (typeof navigator.permissions !== 'undefined') {
        const result = await navigator.permissions.query({ name: 'geolocation' })
        if (result.state === 'granted') {
          setPermissionState('granted')
          await fetchLocation()
        } else if (result.state === 'denied') {
          setPermissionState('denied')
        } else {
          // 'prompt' — show the Enable button, wait for user gesture
          setPermissionState('prompt')
        }
      } else {
        // Permissions API not supported — show button, user gesture will trigger prompt
        setPermissionState('prompt')
      }
    } catch {
      setPermissionState('prompt')
    }
  }

  async function fetchLocation() {
    setIsLoading(true)
    setError(null)
    try {
      const position = await getCurrentLocation()
      setLocation(position.coords.latitude, position.coords.longitude)
      setPermissionState('granted')
    } catch (err) {
      const geoError = err as GeolocationPositionError
      if (geoError.code === geoError.PERMISSION_DENIED) {
        setPermissionState('denied')
        setError(null)
      } else if (geoError.code === geoError.POSITION_UNAVAILABLE) {
        setError('Location unavailable')
      } else if (geoError.code === geoError.TIMEOUT) {
        setError('Location request timed out')
      } else {
        setError('Failed to get location')
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function requestLocation() {
    // Called by user gesture (button tap) — iOS will show the prompt if permission is 'prompt'.
    // If denied, iOS rejects immediately and we show Settings instructions.
    setIsLoading(true)
    setError(null)
    try {
      const position = await getCurrentLocation()
      setLocation(position.coords.latitude, position.coords.longitude)
      setPermissionState('granted')
    } catch (err) {
      const geoError = err as GeolocationPositionError
      if (geoError.code === geoError.PERMISSION_DENIED) {
        setPermissionState('denied')
        setError(null)
        const isStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
        toast(
          isStandalone
            ? 'Location blocked. Go to Settings > Privacy & Security > Location Services to re-enable.'
            : 'Location blocked. Go to Settings > Privacy & Security > Location Services > Safari to enable.',
          { duration: 6000 }
        )
      } else if (geoError.code === geoError.POSITION_UNAVAILABLE) {
        setError('Location unavailable')
      } else if (geoError.code === geoError.TIMEOUT) {
        setError('Location request timed out')
      } else {
        setError('Failed to get location')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    latitude,
    longitude,
    error,
    isLoading,
    permissionState,
    hasLocation: latitude !== null && longitude !== null,
    requestLocation,
  }
}
